import { ErrorCode } from './errors/error-codes';
import { logger } from './logging.service';

export const currentScriptSrc = document.currentScript && (document.currentScript as HTMLScriptElement).src;

const environments = ['dev', 'staging', 'prod', 'uat', 'local'] as const;
export type EnvironmentType = (typeof environments)[number];

const apiUrls: Record<EnvironmentType, string> = {
  local: 'http://localhost:8081',
  dev: 'https://module.api.uat.opel.carusseldwt.com',
  uat: 'https://module.api.uat.opel.carusseldwt.com',
  staging: 'https://module.api.staging.carusseldwt.com',
  prod: 'https://module.api.prod.opel.carusseldwt.com',
};

const instaFileApiUrls: Record<EnvironmentType, string> = {
  local: 'https://bnpl-staging.instacash.hu/api/file/public',
  dev: 'https://bnpl-staging.instacash.hu/api/file/public',
  uat: 'https://bnpl-staging.instacash.hu/api/file/public',
  staging: 'https://staging.instacash.hu/api/file/public',
  prod: 'https://staging.instacash.hu/api/file/public',
};

export class EnvironmentService {
  public get assetsHost() {
    const currentHost = window.location.protocol + '//' + window.location.host;
    if (this.isLocalhost) {
      return currentHost;
    }
    if (this.isGaeDemoUrl) {
      return currentHost;
    }
    if (currentScriptSrc) {
      return new URL(currentScriptSrc).origin;
    }
    return this.queryScriptSource(); // Should not happen
  }

  public get isGaeDemoUrl() {
    return window.location.host.includes('carussel-dwt.appspot.com');
  }

  public get isLocalhost() {
    return window.location.host.includes('localhost');
  }
  public devMode = false;

  public getApiUrl(environment: EnvironmentType) {
    return apiUrls[environment];
  }

  public getLoginUrl(environment: EnvironmentType, adminMode: boolean | undefined) {
    const env = environment === 'local' ? 'dev' : environment;
    if (adminMode) {
      return apiUrls[env] + '/auth/v1/auth/admin/login';
    }
    return apiUrls[env] + '/auth/v1/auth/login';
  }

  public getFileUrl(environment: EnvironmentType, fileId: string) {
    if (!fileId || fileId === 'null') {
      return null;
    }
    return `${instaFileApiUrls[environment]}/${fileId}`;
  }

  public toAbsoluteUrl(asset: string) {
    try {
      return new URL(asset, this.assetsHost || undefined).toString();
    } catch (error) {
      logger.error(
        ErrorCode.AssetAbsoluteUrlCreationFailed,
        `Failed to create absolute URL for ${asset} for host: ${this.assetsHost} at script: ${currentScriptSrc}.`,
        error
      );
      return asset;
    }
  }

  public getValidEnvironment(custom: string | undefined) {
    if (custom && environments.includes(custom as EnvironmentType)) {
      return custom as EnvironmentType;
    }
  }

  private queryScriptSource() {
    try {
      const firstSrc = Array.from(document.getElementsByTagName('script'))
        .map((el) => el.src)
        .find((src) => (src || '').includes('carussel-dwt.appspot.com/dwt-app.js'));
      if (!firstSrc) {
        return '';
      }
      return new URL(firstSrc).origin;
    } catch {
      logger.error(ErrorCode.MissingEntryScriptSource, 'Unable to find script source');
      return '';
    }
  }
}

export const environmentSvc = new EnvironmentService();
